import { DEVICE_SPECIFICATION_NAMES } from '../../common/utils/deviceSpecificationUtilsCoCo.js';
import { getLocalizedString, getProductOfferingPriceDataFromType } from '../../common/utils/productOfferingsUtils.js';
import { toKauppaSlug } from '../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import type { InstallmentOption } from '../../generated/api/installmentOption.js';
import type { OfferBadge } from '../../generated/api/offerBadge.js';
import type { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';

export type DeviceProductOffer = ProductOfferingsResponse & {
  offerBadge?: OfferBadge;
  isPaidByEmployer?: boolean;
};

export interface ProductVariant {
  variant: string;
  variantTitle: string;
}

export type EnergyLabelRating = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g';

export const PRODUCT_GRID_BADGES = {
  FIVE_G: '5g',
  REFURBISHED: 'refurbished',
  PAID_BY_EMPLOYER: 'paidByEmployer',
  DISCOUNT: 'discount',
} as const;

export type ProductGridBadge = (typeof PRODUCT_GRID_BADGES)[keyof typeof PRODUCT_GRID_BADGES];

export const PRODUCT_GRID_BADGE_PRIORITY: readonly ProductGridBadge[] = [
  PRODUCT_GRID_BADGES.FIVE_G,
  PRODUCT_GRID_BADGES.REFURBISHED,
  PRODUCT_GRID_BADGES.PAID_BY_EMPLOYER,
  PRODUCT_GRID_BADGES.DISCOUNT,
];

export const getLowestInstallmentOption = (price: ProductOfferingPrice): InstallmentOption | undefined => {
  const options = (price?.installmentOption ?? []).filter(option => (option.numberOfInstallment ?? 0) > 1);

  if (options.length === 0) {
    return undefined;
  }

  return options.reduce<InstallmentOption>(
    (lowest, current) =>
      (current.priceWithoutTax ?? Infinity) < (lowest.priceWithoutTax ?? Infinity) ? current : lowest,
    options[0]
  );
};

const getColorVariants = (offer: DeviceProductOffer) =>
  offer.productSpecification?.find(spec => spec.name?.toLowerCase() === 'color')?.value?.[0]?.value ?? '';

export const getProductUrl = (offer: DeviceProductOffer): string => {
  const name = getLocalizedString(offer.name);
  if (!name || name.trim() === '') {
    return '';
  }
  return `/${toKauppaSlug(name)}`;
};

export const toProductVariants = (offer: DeviceProductOffer): ProductVariant[] => {
  const variant = getColorVariants(offer);
  if (!variant) {
    return [];
  }
  return [
    {
      variant,
      variantTitle:
        getLocalizedString(
          offer.productSpecification?.find(spec => spec.name?.toLowerCase() === 'color')?.type ?? ''
        ) || variant,
    },
  ];
};

export const getDeviceBrand = (offer: DeviceProductOffer): string =>
  offer.productSpecification?.find(spec => spec.name?.toLowerCase() === DEVICE_SPECIFICATION_NAMES.BRAND)?.value?.[0]
    ?.value ?? '';

export const getOfferPriceEuros = (offer: DeviceProductOffer): number | undefined => {
  const price = getProductOfferingPriceDataFromType(offer.productOfferingPrice, PriceType.ONE_TIME_CHARGE);
  return price?.priceWithoutTax;
};

const getProductSpecificationValues = (offer: DeviceProductOffer, specificationName: string): string[] =>
  (offer.productSpecification ?? [])
    .filter(specification => specification.name?.toLowerCase() === specificationName.toLowerCase())
    .flatMap(specification => specification.value ?? [])
    .map(value => value.value?.trim().toLowerCase())
    .filter((value): value is string => Boolean(value));

export const getEnergyLabelRating = (
  offer: DeviceProductOffer | ProductOfferingsResponse
): EnergyLabelRating | undefined => {
  const normalizedValue = getProductSpecificationValues(offer, 'energyLabel')[0]?.replace(/\s+/g, '');
  const rating = normalizedValue ? /[a-g]/.exec(normalizedValue)?.[0] : undefined;

  return rating as EnergyLabelRating | undefined;
};

export const has5GBadge = (offer: DeviceProductOffer): boolean =>
  getProductSpecificationValues(offer, 'technology').some(value => value.includes('5g'));

export const hasRefurbishedBadge = (offer: DeviceProductOffer): boolean =>
  getProductSpecificationValues(offer, 'deviceCondition').includes('refurbished');

export const getProductGridBadges = (offer: DeviceProductOffer): ProductGridBadge[] =>
  PRODUCT_GRID_BADGE_PRIORITY.filter(badge => {
    switch (badge) {
      case PRODUCT_GRID_BADGES.FIVE_G:
        return has5GBadge(offer);
      case PRODUCT_GRID_BADGES.REFURBISHED:
        return hasRefurbishedBadge(offer);
      case PRODUCT_GRID_BADGES.PAID_BY_EMPLOYER:
        return Boolean(offer.isPaidByEmployer);
      case PRODUCT_GRID_BADGES.DISCOUNT:
        return Boolean(offer.offerBadge);
      default:
        return false;
    }
  }).slice(0, 2);
